/**
 * By default, Remix will handle hydrating your app on the client for you.
 * You are free to delete this file if you'd like to, but if you ever want it revealed again, you can run `npx remix reveal` ✨
 * For more information, see https://remix.run/file-conventions/entry.client
 */
import "@af-utils/scrollend-polyfill"; // as of 2025-01-09 safari still doesn't support scrollend event
import { RemixBrowser } from "@remix-run/react";
import * as Sentry from "@sentry/remix";
import { startTransition, StrictMode } from "react";
import { hydrateRoot } from "react-dom/client";
import { getRootLoaderDataFromWindow } from "./utils";

const rootLoader = getRootLoaderDataFromWindow();

Sentry.init({
  dsn: rootLoader.data?.sentryDSN,
  release: rootLoader.data?.sentryRelease,
  environment: rootLoader.data?.sentryEnvironment,
  // Stale-deploy chunk loads and crawler-generated import failures are not
  // actionable (see DASHBOARD-WORKER-66: 100% bot traffic, 0 real users).
  ignoreErrors: [
    /Failed to fetch dynamically imported module/,
    /Importing a module script failed/,
    /error loading dynamically imported module/,
    /Unable to preload CSS/,
  ],
  // Cloudflare injects Zaraz's tag manager at /cdn-cgi/zaraz/s.js on the zone,
  // and it rejects a promise our global onunhandledrejection handler then
  // reports as ours. That is what DASHBOARD-WORKER-EN turned out to be
  // (`SyntaxError: The string did not match the expected pattern.`, stack
  // `/cdn-cgi/zaraz/s.js:5:233` → `[native code] (json)`, still arriving at
  // release 0.83.0 on Mobile Safari 26 — i.e. after #1371 fixed our own half of
  // that message). Nothing in our bundle is on that stack and we cannot patch a
  // script Cloudflare injects, so filter the whole /cdn-cgi/ path rather than
  // muting the message: a real one of ours must still page us. Archiving the
  // two existing groups only silences the fingerprints we have already seen —
  // this is what stops the next Zaraz variant opening a third (#1213).
  denyUrls: [/\/cdn-cgi\//],
  integrations: [
    Sentry.browserTracingIntegration({
      enableHTTPTimings: true,
      instrumentPageLoad: true,
      traceFetch: true,
    }),
  ],
  replaysSessionSampleRate: 0.05,
  replaysOnErrorSampleRate: 1.0,
});

import("@sentry/remix").then((lazyLoaded) => {
  Sentry.addIntegration(lazyLoaded.replayIntegration());
  Sentry.addIntegration(lazyLoaded.extraErrorDataIntegration());
});

Sentry.setUser({
  id: rootLoader.data?.sentryUserId,
  username: rootLoader.data?.sentryUsername,
});

const originalFetch = window.fetch;
window.fetch = (input, init) => {
  if (input instanceof Request) {
    const newHeaders = new Headers(input.headers);
    if (!newHeaders.has("x-moneta-proxy-target")) {
      newHeaders.set("x-moneta-proxy-target", "remix");
    }
    const modifiedRequest = new Request(input, { headers: newHeaders });
    return originalFetch(modifiedRequest, init);
  } else {
    const modifiedOptions = init ?? {};
    modifiedOptions.headers = new Headers(modifiedOptions.headers);
    if (!modifiedOptions.headers.has("x-moneta-proxy-target")) {
      modifiedOptions.headers.set("x-moneta-proxy-target", "remix");
    }
    return originalFetch(input, modifiedOptions);
  }
};

startTransition(() => {
  hydrateRoot(
    document,
    <StrictMode>
      <RemixBrowser />
    </StrictMode>,
  );
});
